import { useRef } from 'react';
import { Avatar, Box, Skeleton, Typography } from '@mui/material';
import { useEngagementManager } from 'dashboard409a/hooks/useEngagementManager';
import { getInitials } from 'utilities';
import { EngagementManagerCellProps } from './cells.props';
import { PersonSearchPopover } from './personSearchPopover';

const EngagementManagerCell = ({ valuationRow, onEngagementManagerUpdate }: EngagementManagerCellProps) => {
  const cellRef = useRef<HTMLDivElement>(null);

  const {
    anchorEl,
    searchTerm,
    searching,
    engagementManagers,
    open,
    popoverId,
    handleClick,
    handleClose,
    handleSelectEngagementManager,
    handleSearch,
    handleKeyDown,
    setSearchTerm,
  } = useEngagementManager({ valuationRow, onEngagementManagerUpdate });

  if (valuationRow.is_loading_additional_data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Box>
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );
  }

  const cellContent =
    valuationRow.engagement_manager && valuationRow.engagement_manager !== '-' ? (
      <Box
        ref={cellRef}
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        }}>
        <Avatar
          alt={valuationRow.engagement_manager}
          src={
            valuationRow.engagement_manager_image_src && valuationRow.engagement_manager_image_src !== '-'
              ? valuationRow.engagement_manager_image_src
              : undefined
          }
          sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
          {getInitials({ name: valuationRow.engagement_manager })}
        </Avatar>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            {valuationRow.engagement_manager}
          </Typography>
        </Box>
      </Box>
    ) : (
      <Box
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        }}>
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      </Box>
    );

  const results = engagementManagers.map(engagementManager => ({
    name: engagementManager.name,
    targetRecordId: engagementManager.targetRecordId,
    imageSrc: engagementManager.imageSrc,
  }));

  return (
    <>
      {cellContent}
      <PersonSearchPopover
        open={open}
        anchorEl={anchorEl}
        popoverId={popoverId}
        onClose={handleClose}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        searching={searching}
        onSearch={handleSearch}
        onKeyDown={handleKeyDown}
        results={results}
        selectionMode="single"
        onSelect={handleSelectEngagementManager}
        placeholder="Search for engagement manager..."
        helpText="Search and select an engagement manager."
        minWidth={300}
        showSearchIcon
      />
    </>
  );
};

export default EngagementManagerCell;
